import { getBuyNowQueryParamsCookieDomain } from './getBuyNowQueryParamsCookieDomain';
import { SessionStorageBase } from '../_shared';
import { urlParamKeys } from '../UrlParams/constants';

const STORAGE_KEY = 'buynow-query-params';
const COOKIE_KEY = 'buynow-query-params';
const COOKIE_MAX_AGE_SECONDS = 30 * 60;

export const buyNowQueryParamKeys = [
  urlParamKeys.paymentPeriod,
  urlParamKeys.contactCount,
  urlParamKeys.planType,
  urlParamKeys.emailPlanId,
  urlParamKeys.currency,
  urlParamKeys.nonprofit,
  urlParamKeys.promoCode,
  urlParamKeys.promotionCd,
  urlParamKeys.addons,
] as const;

export type BuyNowQueryParamKey = (typeof buyNowQueryParamKeys)[number];

type StoredBuyNowQueryParams = Partial<Record<BuyNowQueryParamKey, string>>;

export class BuyNowQueryParamsSessionStorage extends SessionStorageBase {
  public saveFromSearchParams(search: string) {
    const normalizedSearch = search.startsWith('?') ? search.slice(1) : search;
    const params = new URLSearchParams(normalizedSearch);
    const stored = this.loadAll() ?? {};
    let hasNewParams = false;

    buyNowQueryParamKeys.forEach((key) => {
      const value = params.get(key);

      if (value) {
        stored[key] = value;
        hasNewParams = true;
      }
    });

    if (hasNewParams) {
      this.persist(stored);
    }
  }

  public mergeIntoSearchParams(searchParams: URLSearchParams) {
    const stored = this.loadAll();

    if (!stored) return;

    buyNowQueryParamKeys.forEach((key) => {
      const value = stored[key];

      if (value && !searchParams.has(key)) {
        searchParams.set(key, value);
      }
    });
  }

  public restoreMissingParamsToUrl() {
    const stored = this.loadAll();

    if (!stored) return;

    const searchParams = new URLSearchParams(window.location.search);
    let changed = false;

    buyNowQueryParamKeys.forEach((key) => {
      const value = stored[key];

      if (value && !searchParams.has(key)) {
        searchParams.set(key, value);
        changed = true;
      }
    });

    if (!changed) return;

    const queryString = searchParams.toString();

    window.history.replaceState({}, '', `${window.location.pathname}${queryString ? `?${queryString}` : ''}`);
  }

  public getPaymentPeriod(): string | null {
    return this.getParam(urlParamKeys.paymentPeriod);
  }

  public getParam(key: BuyNowQueryParamKey): string | null {
    const stored = this.loadAll();

    return stored?.[key] ?? null;
  }

  public clear() {
    super.clear(STORAGE_KEY);
    this.clearCookie();
  }

  private persist(stored: StoredBuyNowQueryParams) {
    this.save(STORAGE_KEY, stored);
    this.saveToCookie(stored);
  }

  private loadAll(): StoredBuyNowQueryParams | null {
    const fromCookie = this.loadFromCookie();
    const fromSession = this.loadStored();
    const merged = { ...fromCookie, ...fromSession };

    return Object.keys(merged).length > 0 ? merged : null;
  }

  private loadStored(): StoredBuyNowQueryParams | null {
    try {
      const stored = this.load(STORAGE_KEY);

      if (!stored || typeof stored !== 'object') return null;

      return stored as StoredBuyNowQueryParams;
    } catch {
      // Handle corrupted session storage data by clearing it
      this.clear();

      return null;
    }
  }

  private saveToCookie(stored: StoredBuyNowQueryParams) {
    const domain = getBuyNowQueryParamsCookieDomain();

    if (!domain) return;

    const encodedValue = encodeURIComponent(JSON.stringify(stored));

    document.cookie = `${COOKIE_KEY}=${encodedValue}; domain=${domain}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
  }

  private loadFromCookie(): StoredBuyNowQueryParams | null {
    const cookiePrefix = `${COOKIE_KEY}=`;
    const cookieEntry = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(cookiePrefix));

    if (!cookieEntry) return null;

    try {
      const rawValue = decodeURIComponent(cookieEntry.slice(cookiePrefix.length));
      const parsed = JSON.parse(rawValue) as StoredBuyNowQueryParams;

      if (!parsed || typeof parsed !== 'object') return null;

      return parsed;
    } catch {
      return null;
    }
  }

  private clearCookie() {
    const domain = getBuyNowQueryParamsCookieDomain();

    if (!domain) return;

    document.cookie = `${COOKIE_KEY}=; domain=${domain}; path=/; max-age=0; SameSite=Lax`;
  }
}

export const buyNowQueryParamsSessionStorage = new BuyNowQueryParamsSessionStorage();
